import { Identifiable } from 'gf-types';
import { FieldTypes } from '../../../../helpers/constants';
import { getIn, setIn } from '../../../../helpers/helpers';
import { DataLayoutPosition, TemplateField } from '../../../../types/template-types';
import { getDataLayoutPositionKeyPathsByFieldId } from '../../../selectors';
import { TemplateEditorPayload, TemplateState } from '../../../templateeditor-types';
import { getParentKeyPath } from '../../../helpers';
import {
  createCheckboxGroupField,
  getOriginalCheckboxFields,
  createCheckboxItemsForGroup,
  findRemovedItems,
  processRemovedCheckboxItem,
  separateCheckboxGroupAndFields,
  addCheckboxesToGroup,
  createIndividualCheckboxFields,
  updateFieldPositionsWhenUngrouping,
  prepareDataLayoutPositionsForUngrouping,
  sortCheckboxGroupItems,
  createIndividualDataLayoutPositions,
  updateSectionPositionsWithNewDlps,
  updateTemplateFieldsAfterUngrouping
} from './updater-helpers';
import { validateFieldsAreCheckboxes } from './validators';

export function createCheckboxGroup(state: TemplateState, payload: TemplateEditorPayload): TemplateState {
  const { keyPaths, name: groupName } = payload;

  // Validate keyPaths exist
  if (keyPaths?.length === 0) {
    return state;
  }

  const originalCheckboxFields = getOriginalCheckboxFields(state, keyPaths);

  if (originalCheckboxFields?.length === 0) {
    return state;
  }

  // Validate fields are checkboxes
  if (!validateFieldsAreCheckboxes(originalCheckboxFields)) {
    return state;
  }

  // Create the checkbox group field
  const checkboxGroupField = createCheckboxGroupField(groupName, originalCheckboxFields);

  // Add the checkboxes to the group field and update state
  return addCheckboxesToGroup(state, originalCheckboxFields, checkboxGroupField);
}

export function addCheckboxGroupItems(state: TemplateState, payload: TemplateEditorPayload): TemplateState {
  const { keyPaths } = payload;
  const value = payload.value as Identifiable[];

  for (const keyPath of keyPaths) {
    // Get old items and update with new ones
    const oldItems = getIn<Identifiable[]>(state, keyPath) || [];
    setIn(state, keyPath, value);

    // Find items that were removed
    const removedItems = findRemovedItems(oldItems, value);

    // Process each removed item
    for (const removedItem of removedItems) {
      state = processRemovedCheckboxItem(state, removedItem);
    }
  }

  return state;
}

export function ungroupCheckboxItems(state: TemplateState, payload: TemplateEditorPayload): TemplateState {
  const { keyPaths: checkBoxGroupFieldKeyPaths } = payload;
  // selection of checkboxgroup fields should have the same keyPath
  const checkBoxGroupFieldKeyPath = checkBoxGroupFieldKeyPaths[0] ?? [];
  if (checkBoxGroupFieldKeyPath.length === 0) {
    return state;
  }

  const checkBoxGroupField = getIn<TemplateField>(state, checkBoxGroupFieldKeyPath);
  if (checkBoxGroupField?.items?.length === 0 || checkBoxGroupField?.type !== FieldTypes.CheckBoxGroup) {
    return state;
  }

  const groupFieldId = checkBoxGroupField.id;

  // Create new individual CheckBox fields from the group's items
  const newIndividualCheckboxFields = createIndividualCheckboxFields(checkBoxGroupField.items, state);

  // Update FieldPositions (Form View)
  updateFieldPositionsWhenUngrouping(state, groupFieldId, newIndividualCheckboxFields);

  // Update DataLayoutPositions (List View)
  const sectionsToUpdate = prepareDataLayoutPositionsForUngrouping(state, groupFieldId);

  // Sort items by their sort number
  const sortedItems = sortCheckboxGroupItems(checkBoxGroupField.items);

  // Create individual data layout positions for each new checkbox field
  const groupDataLayoutPositionKeyPaths = getDataLayoutPositionKeyPathsByFieldId(state, groupFieldId);
  for (const groupDlpKeyPath of groupDataLayoutPositionKeyPaths) {
    const groupDlp = getIn<DataLayoutPosition>(state, groupDlpKeyPath);
    if (!groupDlp) continue;

    const parentSectionPositionsKeyPath = getParentKeyPath(groupDlpKeyPath);
    if (!parentSectionPositionsKeyPath) continue;
    const sectionKeyString = JSON.stringify(parentSectionPositionsKeyPath);

    // Create individual DLPs for each checkbox item in this section
    const newDlps = createIndividualDataLayoutPositions(groupDlp, sortedItems);
    sectionsToUpdate.get(sectionKeyString)?.newDlps.push(...newDlps);
  }

  // Update section positions with the new data layout positions
  updateSectionPositionsWithNewDlps(state, sectionsToUpdate);

  // Update template fields to replace checkbox group with individual fields
  updateTemplateFieldsAfterUngrouping(state, groupFieldId, newIndividualCheckboxFields);

  return state;
}

export function addExistingCheckboxesToGroup(state: TemplateState, payload: TemplateEditorPayload): TemplateState {
  const { keyPaths } = payload;

  // Split keypaths into checkbox group and individual checkboxes
  const { checkboxGroupFieldKeyPath, checkboxFields } = separateCheckboxGroupAndFields(state, keyPaths);

  // Validate we have one checkbox group and at least one checkbox
  if (!checkboxGroupFieldKeyPath || checkboxFields?.length === 0) {
    return state;
  }

  const checkBoxGroupField = getIn<TemplateField>(state, checkboxGroupFieldKeyPath);
  if (checkBoxGroupField?.type !== FieldTypes.CheckBoxGroup) {
    return state;
  }

  // Create new items for the checkbox group based on the checkboxes
  const newCheckboxItems = createCheckboxItemsForGroup(checkBoxGroupField, checkboxFields);

  // Add the new items to the group
  checkBoxGroupField.items.push(...newCheckboxItems);

  // Update the checkbox group field in state
  setIn(state, checkboxGroupFieldKeyPath, checkBoxGroupField);

  // Add checkboxes to the group and update state
  return addCheckboxesToGroup(state, checkboxFields, checkBoxGroupField, false);
}
